import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Calendar, Building, MessageSquare } from 'lucide-react';
import { mockStats } from '../mock';
import { useInView } from 'react-intersection-observer';
import Tilt from 'react-parallax-tilt';

export const Stats = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  
  const [counters, setCounters] = useState(mockStats.map(() => 0));
  
  useEffect(() => {
    if (inView) {
      mockStats.forEach((stat, index) => {
        // Skip animation for 24/7 (just display it as is)
        if (stat.number === "24/7") {
          setCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = stat.number;
            return newCounters;
          });
          return;
        }
        
        const target = parseInt(stat.number.replace(/[^0-9]/g, ''));
        const duration = 2000;
        const steps = 60;
        const increment = target / steps;
        let current = 0;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }
          setCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = Math.floor(current);
            return newCounters;
          });
        }, duration / steps);
      });
    }
  }, [inView]);
  
  const formatNumber = (num, original) => {
    // If it's already a string (like "24/7"), return as is
    if (typeof num === 'string') return num;
    
    if (original.includes('+')) return `${num.toLocaleString()}+`;
    if (original.includes('%')) return `${num}%`;
    return num.toLocaleString();
  };
  
  return (
    <section className="py-32 mt-20 bg-gradient-to-br from-slate-50 via-white to-emerald-50 relative overflow-hidden">
      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-20 w-72 h-72 bg-emerald-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-10 right-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow-delayed" />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {mockStats.map((stat, index) => {
            const IconComponent = {
              CheckCircle,
              Clock: Calendar,
              Users: Building,
              Phone: MessageSquare
            }[stat.icon] || CheckCircle;
            
            return (
              <Tilt
                key={index}
                className={`transform transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.05}
                transitionSpeed={1500}
                glareEnable={true}
                glareMaxOpacity={0.2}
                glareColor="#10b981"
                glarePosition="all"
                glareBorderRadius="20px"
              >
                <Card 
                  className="relative text-center p-6 border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-white overflow-hidden group"
                  style={{
                    transform: 'translateZ(50px)',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {/* Pulsing glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-blue-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Animated ring */}
                  <div className="absolute inset-0 rounded-lg border-2 border-emerald-400 opacity-0 group-hover:opacity-30 group-hover:scale-105 transition-all duration-700" />
                  
                  <CardContent className="p-0 relative z-10">
                    <div 
                      className="flex justify-center mb-4"
                      style={{ transform: 'translateZ(60px)' }}
                    >
                      <div className="relative">
                        {/* Pulsing background ring */}
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full opacity-20 animate-ping" />
                        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 shadow-lg group-hover:scale-110 transition-transform duration-500">
                          <IconComponent className="h-7 w-7 text-emerald-600" />
                        </div>
                      </div>
                    </div>
                    <div 
                      className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-slate-900 to-emerald-900 bg-clip-text text-transparent mb-2"
                      style={{ transform: 'translateZ(50px)' }}
                    >
                      {formatNumber(counters[index], stat.number)}
                    </div>
                    <div 
                      className="text-sm text-slate-600 font-medium"
                      style={{ transform: 'translateZ(40px)' }}
                    >
                      {stat.label}
                    </div>
                  </CardContent>
                </Card>
              </Tilt>
            );
          })}
        </div>
      </div>
      
      {/* Custom Animations */}
      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(1.1); opacity: 0.3; }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        
        .animate-pulse-slow-delayed {
          animation: pulse-slow 4s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>
    </section>
  );
};
